import type { Locale, MessageKey } from "@chams-pay/contracts";
import { translate } from "@chams-pay/contracts";
import { useEffect, useRef, useState } from "react";

const MOBILE_FEEDBACK_EVENT = "chams:mobile-feedback";
const backClosers: { readonly id: symbol; readonly close: () => void }[] = [];

export type MobileFeedbackKind =
  | "back-to-exit"
  | "choose-student"
  | "download-started"
  | "content-refreshed"
  | "copied"
  | "student-created"
  | "student-updated"
  | "service-amounts-updated"
  | "lifecycle-updated"
  | "payment-recorded"
  | "instrument-settled"
  | "instrument-rejected"
  | "payment-cancelled"
  | "settings-saved"
  | "rollover-completed"
  | "installation-approved"
  | "installation-rejected"
  | "installation-revoked"
  | "access-code-issued"
  | "sync-completed";

const feedbackKey: Record<MobileFeedbackKind, MessageKey> = {
  "back-to-exit": "mobile.backToExit",
  "choose-student": "mobile.chooseStudent",
  "download-started": "mobile.downloadStarted",
  "content-refreshed": "mobile.contentRefreshed",
  copied: "mobile.copied",
  "student-created": "mobile.studentCreated",
  "student-updated": "mobile.studentUpdated",
  "service-amounts-updated": "mobile.serviceAmountsUpdated",
  "lifecycle-updated": "mobile.lifecycleUpdated",
  "payment-recorded": "mobile.paymentRecorded",
  "instrument-settled": "mobile.instrumentSettled",
  "instrument-rejected": "mobile.instrumentRejected",
  "payment-cancelled": "mobile.paymentCancelled",
  "settings-saved": "mobile.settingsSaved",
  "rollover-completed": "mobile.rolloverCompleted",
  "installation-approved": "mobile.installationApproved",
  "installation-rejected": "mobile.installationRejected",
  "installation-revoked": "mobile.installationRevoked",
  "access-code-issued": "mobile.accessCodeIssued",
  "sync-completed": "mobile.syncCompleted",
};

export function dispatchAppBack(): boolean {
  const active = backClosers.pop();
  if (!active) return false;
  active.close();
  return true;
}

export function useAppBackClose(opened: boolean, onClose: () => void): void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!opened) return;
    const entry = { id: Symbol("app-back-closer"), close: () => onCloseRef.current() };
    backClosers.push(entry);
    return () => {
      const index = backClosers.findIndex((candidate) => candidate.id === entry.id);
      if (index >= 0) backClosers.splice(index, 1);
    };
  }, [opened]);
}

export function showMobileFeedback(kind: MobileFeedbackKind): void {
  window.dispatchEvent(
    new CustomEvent<MobileFeedbackKind>(MOBILE_FEEDBACK_EVENT, { detail: kind }),
  );
}

export function MobileFeedbackPill({ locale }: { readonly locale: Locale }) {
  const [message, setMessage] = useState<MobileFeedbackKind | null>(null);
  const [visible, setVisible] = useState(false);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const clearTimers = () => {
      for (const timer of timers.current) window.clearTimeout(timer);
      timers.current = [];
    };
    const show = (event: Event) => {
      clearTimers();
      const kind = (event as CustomEvent<MobileFeedbackKind>).detail;
      setMessage(kind);
      setVisible(true);
      timers.current = [
        window.setTimeout(() => setVisible(false), 1_900),
        window.setTimeout(() => setMessage(null), 2_250),
      ];
    };
    window.addEventListener(MOBILE_FEEDBACK_EVENT, show);
    return () => {
      window.removeEventListener(MOBILE_FEEDBACK_EVENT, show);
      clearTimers();
    };
  }, []);

  if (!message) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      className={`mobile-feedback-pill ${visible ? "visible" : ""}`}
    >
      <span>{translate(locale, feedbackKey[message])}</span>
    </div>
  );
}
